/**
 * Step - Customer Details
 *
 * Single-card layout: email (with existing consumer lookup), name, phone, address.
 * All simple fields render through MetadataField for centralised control.
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, UserCircle } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { useConsumerLookup } from "@/admin/hooks/use-products";
import { useExternalRoles } from "@/admin/hooks/use-external-roles";
import { useEntityTypeByKey } from "@/admin/hooks/use-entities";
import {
  useDataCaptureConfig,
  isCollapsed,
  isVisible,
  isRequired,
} from "@/admin/hooks/use-data-capture-config";
import type { WizardState } from "@/admin/pages/registrations/new-registration-page";
import type { DataCaptureConfig } from "@/admin/hooks/use-registration-settings";
import {
  RegistrationDisclosureSection,
  RegistrationPanel,
  RegistrationStepActions,
  RegistrationStepGrid,
} from "./layout";
import { InlineDealerField } from "./shared";
import { RegistrantEmailTick } from "./registrant-email-tick";
import { MailingAddressCapture } from "./mailing-address-capture";
import { normaliseAddressObject } from "./address-utils";
import { MetadataField } from "@/shared/lib/forms";
import type { FormFieldMeta } from "@/shared/lib/forms";
import {
  isValidPhone,
  PHONE_VALIDATION_MESSAGE,
  splitStoredValue,
} from "@/shared/lib/forms/phone";
import type { RegistrationFormMetadata } from "@/admin/types/products";
import {
  coreContactConfigMap,
  contactBackendValues,
  resolveContactField,
} from "./contact-field-config";
import {
  useRequiredFieldErrors,
  type RequiredFieldSpec,
} from "./wizard-validation";

/* ── Props ───────────────────────────────────────────────── */

interface StepProps {
  state: WizardState;
  update: (payload: Partial<WizardState>) => void;
  next: () => void;
  back?: () => void;
  continueDisabled?: boolean;
  continueHint?: string | null;
  dataCaptureConfig?: DataCaptureConfig;
  /**
   * Holding-form-schema metadata. Its `consumer_fields` carry the core-contact
   * attributes' `configuration_data` (`required_when` / `visible_when`), which
   * drives conditional required/visible on email, name, and phone.
   */
  metadata?: RegistrationFormMetadata | null;
  earlyCaptureFields?: string[];
  /**
   * True when the installer/contractor is captured earlier in the flow
   * (host-first flows render it on the Property & Details step). Suppresses
   * the duplicate contractor picker on this step.
   */
  installerCapturedEarly?: boolean;
  /**
   * Extra panel(s) to render alongside this step's own panel in the
   * shared `RegistrationStepGrid`. Lets the page slot in the shared
   * `RegistrationFieldsPanel` so it sits to the right rather than below.
   */
  extraPanels?: React.ReactNode;
}

/* ── Component ───────────────────────────────────────────── */

export function StepCustomer({
  state,
  update,
  next,
  back,
  // The legacy `continueDisabled` prop is intentionally ignored here.
  // The Continue button is always enabled now and validation happens
  // via the wizard validation context (inline per-field errors).
  continueHint,
  dataCaptureConfig,
  metadata,
  earlyCaptureFields = [],
  installerCapturedEarly = false,
  extraPanels,
}: StepProps) {
  const { t } = useTranslation();
  const { dcc, toggleField, isFieldShown, fieldLabel } =
    useDataCaptureConfig(dataCaptureConfig);

  /* --- Contractor picker ---
     Skip it entirely when the installer/contractor is captured earlier in the
     flow (host-first flows render the same InlineDealerField on the Property &
     Details step, driven by the template's early_registration_attributes).
     Showing it again here would duplicate the field. Tenants without early
     installer capture keep the picker on this step. */
  const { data: externalRoles = [] } = useExternalRoles();
  const contractorRole = externalRoles[0] ?? null;
  // Keep the panel mounted once a partnership is selected - InlineDealerField
  // renders its own selected-state chip (with a clear button) when
  // `state.partnershipUuid` is set. Gating on `!state.partnershipUuid` here
  // would unmount the whole panel the moment a retailer is picked, making the
  // selection appear to vanish.
  const showContractorPicker =
    contractorRole !== null && !installerCapturedEarly;

  /* --- Email lookup --- */
  // Seeded from the stored email so a revisit knows whose it is again.
  const [emailForLookup, setEmailForLookup] = useState(() => {
    const stored = state.consumerEmail.trim().toLowerCase();
    return stored.includes("@") ? stored : "";
  });
  const [prefilled, setPrefilled] = useState(false);
  const { data: lookup } = useConsumerLookup(
    emailForLookup,
    state.partnershipUuid,
  );
  // The registrant's own email is never a customer to pre-fill: ask whose it
  // is. A lookup for an earlier email says nothing about the one typed now.
  const isRegistrantEmail =
    lookup?.isRegistrantEmail === true &&
    emailForLookup === state.consumerEmail.trim().toLowerCase();
  const existingConsumer = lookup?.customer ?? null;

  /* Address lookup provider sourced from the consumer entity `address`
     attribute (e.g. google_places); undefined falls back to org-routed lookup. */
  const { data: consumerEntity } = useEntityTypeByKey("consumer");
  const addressLookupProvider =
    (consumerEntity?.attributes?.find(
      (a) => a.code_name === "address" && a.field_type === "ADDRESS",
    )?.configuration_data?.lookup_provider as string | undefined) ?? undefined;

  /* Tenant-authored customer fields from the Customers schema (e.g. a
     landline). Address and its component columns render separately below. */
  const extraFields = useMemo((): FormFieldMeta[] => {
    const attrs = consumerEntity?.attributes ?? [];
    const componentCodes = new Set(
      attrs.flatMap((a) =>
        Object.values(
          (a.configuration_data?.component_columns as
            | Record<string, string>
            | undefined) ?? {},
        ),
      ),
    );
    return attrs
      .filter(
        (a) =>
          a.source === "EAV" &&
          a.field_type !== "ADDRESS" &&
          !componentCodes.has(a.code_name),
      )
      .sort((a, b) => a.order - b.order)
      .map((a) => ({
        code_name: a.code_name,
        display_name: a.display_name,
        field_type: a.field_type,
        required: a.required,
        hint: a.hint,
        order: a.order,
        configuration_data: a.configuration_data,
      }));
  }, [consumerEntity?.attributes]);

  /* --- Build field definitions from DataCaptureConfig ---
     Base required/visible come from the tenant DataCaptureConfig; any
     `required_when` / `visible_when` authored on the backend consumer
     attributes (delivered via `metadata.consumer_fields.configuration_data`)
     is layered on top by `resolveContactField`, so e.g. the WPS-118
     phone-or-email rule is honoured with zero bespoke cross-field logic.
     With no config, the resolver returns the base flags unchanged. */

  const contactConfigMap = useMemo(
    () => coreContactConfigMap(metadata?.consumer_fields),
    [metadata?.consumer_fields],
  );
  const contactValues = useMemo(
    () =>
      contactBackendValues({
        consumerEmail: state.consumerEmail,
        consumerFirstName: state.consumerFirstName,
        consumerLastName: state.consumerLastName,
        consumerPhone: state.consumerPhone,
      }),
    [
      state.consumerEmail,
      state.consumerFirstName,
      state.consumerLastName,
      state.consumerPhone,
    ],
  );

  const customerFields = useMemo((): FormFieldMeta[] => {
    const fields: FormFieldMeta[] = [];
    const emailFlags = resolveContactField(
      "consumerEmail",
      contactConfigMap,
      contactValues,
      !dcc || isRequired(dcc.consumer_email),
      isVisible(dcc?.consumer_email),
    );
    if (emailFlags.visible) {
      fields.push({
        code_name: "consumerEmail",
        display_name: fieldLabel("consumer_email", "Email"),
        field_type: "EMAIL",
        required: emailFlags.required,
        hint: "customer@example.com",
        order: 0,
      });
    }
    const nameBaseVisible = isVisible(dcc?.consumer_name);
    const nameBaseRequired = isRequired(dcc?.consumer_name);
    const firstFlags = resolveContactField(
      "consumerFirstName",
      contactConfigMap,
      contactValues,
      nameBaseRequired,
      nameBaseVisible,
    );
    const lastFlags = resolveContactField(
      "consumerLastName",
      contactConfigMap,
      contactValues,
      nameBaseRequired,
      nameBaseVisible,
    );
    if (firstFlags.visible) {
      fields.push({
        code_name: "consumerFirstName",
        display_name: "First Name",
        field_type: "TEXT",
        required: firstFlags.required,
        hint: "First name",
        order: 1,
      });
    }
    if (lastFlags.visible) {
      fields.push({
        code_name: "consumerLastName",
        display_name: "Last Name",
        field_type: "TEXT",
        required: lastFlags.required,
        hint: "Last name",
        order: 2,
      });
    }
    const phoneFlags = resolveContactField(
      "consumerPhone",
      contactConfigMap,
      contactValues,
      isRequired(dcc?.consumer_phone),
      isVisible(dcc?.consumer_phone),
    );
    if (phoneFlags.visible) {
      fields.push({
        code_name: "consumerPhone",
        display_name: fieldLabel("consumer_phone", "Phone Number"),
        field_type: "PHONE",
        required: phoneFlags.required,
        hint: "Phone number",
        order: 3,
      });
    }
    return [...fields, ...extraFields];
  }, [dcc, fieldLabel, contactConfigMap, contactValues, extraFields]);

  /* --- Value accessors mapped to WizardState --- */

  const fieldValueMap: Record<string, unknown> = useMemo(
    () => ({
      ...state.consumerExtraValues,
      consumerEmail: state.consumerEmail,
      consumerFirstName: state.consumerFirstName,
      consumerLastName: state.consumerLastName,
      consumerPhone: state.consumerPhone,
    }),
    [
      state.consumerExtraValues,
      state.consumerEmail,
      state.consumerFirstName,
      state.consumerLastName,
      state.consumerPhone,
    ],
  );

  const fieldUpdateMap: Record<string, (v: unknown) => void> = useMemo(
    () => ({
      ...Object.fromEntries(
        extraFields.map((f) => [
          f.code_name,
          (v: unknown) =>
            update({
              consumerExtraValues: {
                ...state.consumerExtraValues,
                [f.code_name]: v,
              },
            }),
        ]),
      ),
      consumerEmail: (v) =>
        update({ consumerEmail: (v as string) ?? "", consumerEmailIsOwners: null }),
      consumerFirstName: (v) =>
        update({ consumerFirstName: (v as string) ?? "" }),
      consumerLastName: (v) =>
        update({ consumerLastName: (v as string) ?? "" }),
      consumerPhone: (v) => update({ consumerPhone: (v as string) ?? "" }),
    }),
    [update, extraFields, state.consumerExtraValues],
  );

  /* --- Handlers --- */

  const handleEmailBlur = useCallback(() => {
    const trimmed = state.consumerEmail.trim().toLowerCase();
    if (trimmed && trimmed.includes("@")) {
      setEmailForLookup(trimmed);
      setPrefilled(false);
    }
  }, [state.consumerEmail]);

  function handlePrefill() {
    if (!existingConsumer) return;
    const patch: Partial<WizardState> = {
      consumerFirstName: existingConsumer.first_name || state.consumerFirstName,
      consumerLastName: existingConsumer.last_name || state.consumerLastName,
      consumerPhone: existingConsumer.phone_number || state.consumerPhone,
    };
    // Pre-fill the address from the consumer's stored EAV attributes. Address
    // is the custom consumer attribute the wizard renders today; it maps to the
    // canonical `state.address` slot regardless of its code_name, so matching on
    // field_type keeps this tenant-agnostic and resilient to future custom
    // address attributes.
    const addressAttr = Array.isArray(existingConsumer.attribute_values)
      ? existingConsumer.attribute_values.find(
          (attr) => attr.field_type === "ADDRESS" && attr.value != null,
        )
      : undefined;
    if (addressAttr) {
      patch.address = normaliseAddressObject(addressAttr.value);
    }
    const extraCodes = new Set(extraFields.map((f) => f.code_name));
    const storedExtras = Array.isArray(existingConsumer.attribute_values)
      ? existingConsumer.attribute_values.filter(
          (attr) => extraCodes.has(attr.code_name) && attr.value != null,
        )
      : [];
    if (storedExtras.length) {
      patch.consumerExtraValues = {
        ...state.consumerExtraValues,
        ...Object.fromEntries(storedExtras.map((a) => [a.code_name, a.value])),
      };
    }
    update(patch);
    setPrefilled(true);
  }

  /* --- Per-field required validation (driven by the wizard
        validation context). The Continue button is always enabled;
        when clicked, fields below register their required state and
        get inline red-border + hint treatment. */
  const addressCapturedEarly = earlyCaptureFields.includes("consumer_address");
  const addressRequired =
    !addressCapturedEarly &&
    isVisible(dcc?.consumer_address) &&
    isRequired(dcc?.consumer_address);
  const addressFilled =
    !!state.address &&
    Object.values(state.address).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    const specs: RequiredFieldSpec[] = customerFields
      .filter((f) => {
        if (f.required) return true;
        // Also validate optional phone fields once the user has typed
        // any digits - a partially filled phone is rejected regardless
        // of whether the field itself is mandatory.
        if (f.field_type === "PHONE") {
          const val = fieldValueMap[f.code_name];
          const trimmed = typeof val === "string" ? val.trim() : "";
          return (
            splitStoredValue(trimmed).base.length > 0 && !isValidPhone(trimmed)
          );
        }
        return false;
      })
      .map((f) => {
        const val = fieldValueMap[f.code_name];
        const trimmed = typeof val === "string" ? val.trim() : "";
        let isFilled: boolean;
        if (typeof val === "string") {
          isFilled = trimmed.length > 0;
          if (isFilled && f.field_type === "EMAIL") {
            isFilled = trimmed.includes("@");
          }
          if (isFilled && f.field_type === "PHONE") {
            isFilled = isValidPhone(trimmed);
          }
        } else {
          isFilled = !!val;
        }
        let message = `${f.display_name} is required`;
        if (
          f.field_type === "EMAIL" &&
          trimmed.length > 0 &&
          !trimmed.includes("@")
        ) {
          message = "Enter a valid email address";
        } else if (
          f.field_type === "PHONE" &&
          splitStoredValue(trimmed).base.length > 0 &&
          !isValidPhone(trimmed)
        ) {
          message = PHONE_VALIDATION_MESSAGE;
        }
        return {
          key: f.code_name,
          label: f.display_name,
          isFilled,
          message,
        };
      });
    if (addressRequired) {
      specs.push({
        key: "consumer_address",
        label: "Address",
        isFilled: addressFilled,
      });
    }
    return specs;
  }, [customerFields, fieldValueMap, addressRequired, addressFilled]);
  const errors = useRequiredFieldErrors("customer", requiredSpecs);

  /* --- Render --- */

  return (
    <div>
      <RegistrationStepGrid>
        {/* Left column: consumer details with the contractor/retailer picker
            stacked beneath it, so the picker stays within the left slot width
            instead of overflowing full-width under the grid. */}
        <div className="flex flex-col gap-6">
          <RegistrationPanel
            title={t("registrations.steps.customerDetails")}
            icon={<UserCircle size={14} className="text-[var(--ink-3)]" />}
          >
            {/* Simple fields from DataCaptureConfig - rendered via MetadataField */}
            {customerFields.map((field) => {
              const fieldMode = (() => {
                if (field.code_name === "consumerEmail")
                  return dcc?.consumer_email;
                if (
                  field.code_name === "consumerFirstName" ||
                  field.code_name === "consumerLastName"
                ) {
                  return dcc?.consumer_name;
                }
                if (field.code_name === "consumerPhone")
                  return dcc?.consumer_phone;
                return undefined;
              })();

              const collapseKey = (() => {
                if (field.code_name === "consumerEmail") return "email";
                if (
                  field.code_name === "consumerFirstName" ||
                  field.code_name === "consumerLastName"
                )
                  return "name";
                if (field.code_name === "consumerPhone") return "phone";
                return "";
              })();
              const isCollapsible =
                collapseKey !== "" && isCollapsed(fieldMode);
              const isShown = isCollapsible
                ? isFieldShown(collapseKey, fieldMode)
                : true;

              // Skip the second name field - we render both names together
              if (field.code_name === "consumerLastName") return null;

              // Name fields: render side by side
              if (field.code_name === "consumerFirstName") {
                const lastNameField = customerFields.find(
                  (f) => f.code_name === "consumerLastName",
                );
                const content = (
                  <div className="grid grid-cols-2 gap-3">
                    <MetadataField
                      field={field}
                      value={fieldValueMap[field.code_name]}
                      onChange={fieldUpdateMap[field.code_name]}
                      labelVariant="compact"
                      error={errors[field.code_name]}
                    />
                    {lastNameField && (
                      <MetadataField
                        field={lastNameField}
                        value={fieldValueMap[lastNameField.code_name]}
                        onChange={fieldUpdateMap[lastNameField.code_name]}
                        labelVariant="compact"
                        error={errors[lastNameField.code_name]}
                      />
                    )}
                  </div>
                );

                if (isCollapsible) {
                  return (
                    <RegistrationDisclosureSection
                      key="name-row"
                      label={`Add ${fieldLabel("consumer_name", "Name")}`}
                      open={isShown}
                      onToggle={() => toggleField("name")}
                    >
                      {content}
                    </RegistrationDisclosureSection>
                  );
                }

                return <div key="name-row">{content}</div>;
              }

              // Email: needs onBlur for consumer lookup + prefill hint
              if (field.code_name === "consumerEmail") {
                const content = (
                  <div key={field.code_name}>
                    <MetadataField
                      field={field}
                      value={fieldValueMap[field.code_name]}
                      onChange={fieldUpdateMap[field.code_name]}
                      onBlur={handleEmailBlur}
                      labelVariant="compact"
                      error={errors[field.code_name]}
                    />
                    <RegistrantEmailTick
                      email={state.consumerEmail}
                      isRegistrantEmail={isRegistrantEmail}
                      emailIsOwners={state.consumerEmailIsOwners}
                      onChange={(isOwners) =>
                        update({ consumerEmailIsOwners: isOwners })
                      }
                    />
                    {existingConsumer &&
                      !prefilled &&
                      state.consumerEmailIsOwners !== false && (
                      <p className="mt-1.5 text-[11px] text-[var(--green)]">
                        Existing customer&nbsp;-&nbsp;
                        <Button
                          variant="link"
                          type="button"
                          onClick={handlePrefill}
                          className="cursor-pointer border-none bg-transparent p-0 text-[11px] font-light text-[var(--green)] underline transition-all duration-150 ease hover:opacity-80"
                        >
                          pre-fill?
                        </Button>
                      </p>
                    )}
                  </div>
                );

                if (isCollapsible) {
                  return (
                    <RegistrationDisclosureSection
                      key={field.code_name}
                      label={`Add ${field.display_name}`}
                      open={isShown}
                      onToggle={() => toggleField(collapseKey)}
                    >
                      {content}
                    </RegistrationDisclosureSection>
                  );
                }

                return content;
              }

              // Default: render the MetadataField directly
              if (isCollapsible) {
                return (
                  <RegistrationDisclosureSection
                    key={field.code_name}
                    label={`Add ${field.display_name}`}
                    open={isShown}
                    onToggle={() => toggleField(collapseKey)}
                  >
                    <MetadataField
                      field={field}
                      value={fieldValueMap[field.code_name]}
                      onChange={fieldUpdateMap[field.code_name]}
                      labelVariant="compact"
                      error={errors[field.code_name]}
                    />
                  </RegistrationDisclosureSection>
                );
              }

              return (
                <MetadataField
                  key={field.code_name}
                  field={field}
                  value={fieldValueMap[field.code_name]}
                  onChange={fieldUpdateMap[field.code_name]}
                  labelVariant="compact"
                  error={errors[field.code_name]}
                />
              );
            })}

            {/* Address - driven by data_capture_config.consumer_address.
                Skipped when already captured on the host step via early_capture_fields. */}
            {!addressCapturedEarly &&
              isVisible(dcc?.consumer_address) &&
              (isCollapsed(dcc?.consumer_address) ? (
                <RegistrationDisclosureSection
                  label={`Add ${fieldLabel("consumer_address", "Address")}`}
                  open={isFieldShown("address", dcc?.consumer_address)}
                  onToggle={() => toggleField("address")}
                >
                  <div>
                    <Label className="gap-0 mb-2 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                      {fieldLabel("consumer_address", "Address")}
                      {isRequired(dcc?.consumer_address) && (
                        <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
                          *
                        </span>
                      )}
                    </Label>
                    <MailingAddressCapture
                      address={state.address}
                      onChange={(next) => update({ address: next })}
                      error={errors["consumer_address"]}
                      data-testid="customer-address-capture"
                      lookupProvider={addressLookupProvider}
                    />
                  </div>
                </RegistrationDisclosureSection>
              ) : (
                <div>
                  <Label className="gap-0 mb-2 mt-3 block text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    {fieldLabel("consumer_address", "Address")}
                    {isRequired(dcc?.consumer_address) && (
                      <span className="ml-0.5 inline-block mt-2 text-[17px] leading-[4px] text-[var(--status-error)]">
                        *
                      </span>
                    )}
                  </Label>
                  <MailingAddressCapture
                    address={state.address}
                    onChange={(next) => update({ address: next })}
                    error={errors["consumer_address"]}
                    data-testid="customer-address-capture"
                    lookupProvider={addressLookupProvider}
                  />
                </div>
              ))}
          </RegistrationPanel>

          {showContractorPicker && contractorRole && (
            <RegistrationPanel
              title={contractorRole.name}
              icon={<Briefcase size={14} className="text-[var(--ink-3)]" />}
            >
              <InlineDealerField
                state={state}
                update={update}
                label={contractorRole.name}
                searchPlaceholder={`Search ${contractorRole.name.toLowerCase()}s...`}
                emptyMessage={`No ${contractorRole.name.toLowerCase()}s found`}
                searchOptions={{ role: contractorRole.uuid }}
              />
            </RegistrationPanel>
          )}
        </div>

        {extraPanels}
      </RegistrationStepGrid>

      {/* ── Navigation ───────────────────────────────────── */}
      <RegistrationStepActions
        onBack={back}
        onContinue={next}
        continueHint={continueHint}
        withDivider
      />
    </div>
  );
}
